/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren } from 'preact';
import { Layer } from '../../UNSAFE_Layer';
import { Floating, Placement } from '../../UNSAFE_Floating';
import { Ref, useRef, useState } from 'preact/hooks';
import { styles } from '../../hooks/PRIVATE_useDatatip/themes/useDatatip.css';
import { useId } from '../../hooks/UNSAFE_useId';
import { Property } from 'csstype';
import { DATATIP_OFFSET } from '#utils/UNSAFE_visUtils/datatipUtils';

export type Dimension = { x: number; width: number; y: number; height: number };

type DatatipProps = {
  /**
   * The datatip content.
   */
  content?: ComponentChildren;

  /**
   * Whether the datatip is anchored to pointer or element.
   *
   */
  anchor: 'element' | 'pointer';

  /**
   * The offset from the anchor element or pointer.
   */
  offset?: {
    mainAxis?: number;
    crossAxis?: number;
  };

  /**
   * The border color of the datatip.
   */
  borderColor?: Property.BorderColor;

  /**
   * The placement of datatip from anchor point.
   */
  placement?: Placement;

  targetFocusRef: Ref<HTMLElement>;
};

/**
 * The hook to support custom datatip in visualization components.
 * TODO: THIS IS A TEMPORARY FORK OF PRIVATE_useDatatip until Gantt moves state up.
 */
export function useDatatip({
  content,
  placement = 'top-end-corner',
  offset,
  borderColor,
  anchor,
  targetFocusRef
}: DatatipProps) {
  const uniqueIdRef = useRef<string>(useId());
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const offsetValue = offset || { mainAxis: DATATIP_OFFSET, crossAxis: -1 * DATATIP_OFFSET };
  const targetRef = useRef<HTMLElement | null>(null);

  const onPointerMove = (event: PointerEvent) => {
    setCoords({ x: event.clientX, y: event.clientY });
  };

  const onFocus = (event: FocusEvent) => {
    if (event.eventPhase === Event.AT_TARGET || event.eventPhase === Event.BUBBLING_PHASE) {
      targetRef.current = event.target as HTMLElement;
    }
  };

  const onShowFocusDatatip = () => {
    targetRef.current = targetFocusRef.current;
  };

  const onPointerEnter = (event: PointerEvent) => {
    if (event.eventPhase === Event.AT_TARGET || event.eventPhase === Event.BUBBLING_PHASE) {
      targetRef.current = event.currentTarget as HTMLElement;
    }
  };

  // TODO: Why don't others need this? Find a way to remove this workaround
  const onPointerLeave = () => {
    setCoords({ x: -10000, y: -10000 });
  };

  const anchorRef = anchor === 'element' ? targetRef : { current: coords };
  const datatipContent =
    content != null ? (
      <Layer>
        <Floating
          anchorRef={anchorRef}
          placement={placement}
          offsetValue={offsetValue}
          flipOptions={{ crossAxis: true, mainAxis: true }}
          shiftOptions={{ crossAxis: false, mainAxis: false }}>
          <div id={uniqueIdRef.current} className={styles.visDatatipStyle} style={{ borderColor }}>
            {content}
          </div>
        </Floating>
      </Layer>
    ) : null;

  return {
    datatipContent,
    datatipProps: {
      'aria-describedby': uniqueIdRef.current,
      onPointerEnter,
      onPointerLeave,
      onPointerMove,
      onFocus,
      onShowFocusDatatip
    }
  };
}
